import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './supabaseClient';
import './App.css';
import LandingPage from './LandingPage';
import AuthPage from './AuthPage';
import PricingPage from './PricingPage';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';
import { LegalDisclaimer, AboutUs, Contact, FAQ } from './Pages';
import CompanyProfile from './CompanyProfile';
import PortfolioTracker from './PortfolioTracker';
import InsiderTrading from './InsiderTrading';
import FDAFailures from './FDAFailures';
import EarningsCollider from './EarningsCollider';
import WeeklyBrief from './WeeklyBrief';
import NaturalSearch from './NaturalSearch';
import AdComTracker from './AdComTracker';
import NewsFeed from './NewsFeed';
import PriceAlerts from './PriceAlerts';

const T = {
  bg:           '#060d1e',
  surface:      '#0d1a2d',
  surfaceHi:    '#132238',
  surfaceHover: '#172944',
  border:       '#1e3a5f',
  borderHi:     '#2d5a8e',
  text:         '#f0f6ff',
  textSub:      '#8ba5c8',
  textMute:     '#4a6a8a',
  emerald:      '#10b981',
  emeraldDim:   'rgba(16,185,129,0.10)',
  emeraldMid:   'rgba(16,185,129,0.18)',
  blue:         '#3b82f6',
  blueDim:      'rgba(59,130,246,0.10)',
  blueMid:      'rgba(59,130,246,0.18)',
  green:        '#22c55e',
  amber:        '#f59e0b',
  red:          '#ef4444',
  purple:       '#a78bfa',
  teal:         '#2dd4bf',
  gold:         '#fbbf24',
};

const SIDEBAR_W = 248;

function LogoMark({ size = 32 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="32" height="32" rx="8" fill="#0a1020" stroke="#10b981" strokeWidth="1.2"/>
      <polyline points="3,17 7,17 9,17 11,11 13,23 15,16 17,16 19,13 21,18 23,16 26,16 29,14" fill="none" stroke="#10b981" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="29" cy="14" r="1.8" fill="#3b82f6"/>
      <line x1="3" y1="21.5" x2="29" y2="21.5" stroke="#10b981" strokeWidth="0.5" strokeOpacity="0.2"/>
      <rect x="4"  y="26" width="2.5" height="4"   rx="0.8" fill="#10b981" fillOpacity="0.3"/>
      <rect x="8"  y="25" width="2.5" height="5"   rx="0.8" fill="#10b981" fillOpacity="0.45"/>
      <rect x="12" y="24" width="2.5" height="6"   rx="0.8" fill="#10b981" fillOpacity="0.6"/>
      <rect x="16" y="23" width="2.5" height="7"   rx="0.8" fill="#10b981" fillOpacity="0.75"/>
      <rect x="20" y="22" width="2.5" height="8"   rx="0.8" fill="#10b981" fillOpacity="0.9"/>
      <rect x="24" y="21.5" width="2.5" height="8.5" rx="0.8" fill="#10b981"/>
    </svg>
  );
}

const Icon = ({ name, size = 16, color = 'currentColor' }) => {
  const icons = {
    week:      <><rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" fill="none"/><line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="1.5"/></>,
    news:      <><path d="M4 4h16v16H4z" stroke={color} strokeWidth="1.5" fill="none" rx="2"/><line x1="8" y1="9" x2="16" y2="9" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="8" y1="13" x2="16" y2="13" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="8" y1="17" x2="12" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    fda:       <><path d="M12 2L3 7v10l9 5 9-5V7L12 2z" stroke={color} strokeWidth="1.5" fill="none"/><line x1="12" y1="12" x2="12" y2="17" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><circle cx="12" cy="9" r="1.5" fill={color}/></>,
    trials:    <><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill="none"/><path d="M9 12l2 2 4-4" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
    calendar:  <><rect x="3" y="4" width="18" height="18" rx="2" stroke={color} strokeWidth="1.5" fill="none"/><line x1="16" y1="2" x2="16" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="8" y1="2" x2="8" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="10" x2="21" y2="10" stroke={color} strokeWidth="1.5"/><circle cx="8" cy="15" r="1" fill={color}/><circle cx="12" cy="15" r="1" fill={color}/></>,
    adcom:     <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke={color} strokeWidth="1.5" fill="none"/><path d="M23 21v-2a4 4 0 0 0-3-3.87" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><path d="M16 3.13a4 4 0 0 1 0 7.75" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    failures:  <><circle cx="12" cy="12" r="9" stroke={color} strokeWidth="1.5" fill="none"/><line x1="15" y1="9" x2="9" y2="15" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="9" y1="9" x2="15" y2="15" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    insider:   <><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/></>,
    earnings:  <><line x1="12" y1="1" x2="12" y2="23" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    alerts:    <><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/><path d="M13.73 21a2 2 0 0 1-3.46 0" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    watchlist: <><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" stroke={color} strokeWidth="1.5" fill="none" strokeLinejoin="round"/></>,
    portfolio: <><line x1="18" y1="20" x2="18" y2="10" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="12" y1="20" x2="12" y2="4" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="6" y1="20" x2="6" y2="14" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    menu:      <><line x1="3" y1="6" x2="21" y2="6" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="12" x2="21" y2="12" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="3" y1="18" x2="21" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    close:     <><line x1="18" y1="6" x2="6" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round"/><line x1="6" y1="6" x2="18" y2="18" stroke={color} strokeWidth="1.5" strokeLinecap="round"/></>,
    more:      <><circle cx="12" cy="12" r="1" fill={color}/><circle cx="19" cy="12" r="1" fill={color}/><circle cx="5" cy="12" r="1" fill={color}/></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
      {icons[name] || null}
    </svg>
  );
};

const getNavGroups = (upcomingCount, watchlistCount) => [
  {
    label: 'OVERVIEW',
    items: [
      { id: 'week',     label: 'This Week',    icon: 'week'     },
      { id: 'news',     label: 'News Feed',    icon: 'news'     },
    ],
  },
  {
    label: 'FDA INTELLIGENCE',
    items: [
      { id: 'fda',      label: 'FDA Drugs',       icon: 'fda'      },
      { id: 'trials',   label: 'Clinical Trials', icon: 'trials'   },
      { id: 'calendar', label: 'FDA Calendar',    icon: 'calendar', badge: upcomingCount || null },
      { id: 'adcom',    label: 'AdCom Tracker',   icon: 'adcom'    },
      { id: 'failures', label: 'FDA Failures',    icon: 'failures' },
    ],
  },
  {
    label: 'MARKET INTELLIGENCE',
    items: [
      { id: 'insider',  label: 'Insider Trading',  icon: 'insider'  },
      { id: 'earnings', label: 'Earnings Collider', icon: 'earnings' },
      { id: 'alerts',   label: 'Price Alerts',     icon: 'alerts'   },
    ],
  },
  {
    label: 'MY PORTFOLIO',
    items: [
      { id: 'watchlist', label: 'Watchlist', icon: 'watchlist', badge: watchlistCount || null },
      { id: 'portfolio', label: 'Portfolio', icon: 'portfolio' },
    ],
  },
];

function Sidebar({ activeTab, setActiveTab, userPlan, session, onSignOut, navGroups, onClose, isMobile }) {
  return (
    <aside style={{
      width: SIDEBAR_W,
      background: T.surface,
      borderRight: `1px solid ${T.border}`,
      display: 'flex',
      flexDirection: 'column',
      height: '100vh',
      overflowY: 'auto',
      flexShrink: 0,
    }}>
      <div style={{ padding: '20px 20px 16px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <LogoMark size={36} />
          <div>
            <div style={{ fontSize: '17px', fontWeight: '800', color: T.text, letterSpacing: '-0.4px', lineHeight: 1.1 }}>
              Alpha<span style={{ color: T.emerald }}>Bio</span><span style={{ fontWeight: '300', color: T.textSub }}>AI</span>
            </div>
            <div style={{ fontSize: '10px', color: '#6b9aca', letterSpacing: '2px', marginTop: '2px', fontWeight: '600' }}>INTELLIGENCE</div>
          </div>
        </div>
        {isMobile && (
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.textMute, padding: '4px' }}>
            <Icon name="close" size={20} color={T.textMute} />
          </button>
        )}
      </div>

      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '13px', color: T.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
            {session?.user?.email}
          </span>
          <span style={{
            fontSize: '11px', fontWeight: '800', letterSpacing: '0.8px',
            color: userPlan === 'pro' ? T.gold : T.textSub,
            backgroundColor: userPlan === 'pro' ? 'rgba(251,191,36,0.10)' : T.surfaceHi,
            border: `1px solid ${userPlan === 'pro' ? 'rgba(251,191,36,0.25)' : T.border}`,
            padding: '3px 10px', borderRadius: '5px',
          }}>{userPlan.toUpperCase()}</span>
        </div>
      </div>

      <nav style={{ flex: 1, padding: '12px 10px', overflowY: 'auto' }}>
        {navGroups.map((group) => (
          <div key={group.label} style={{ marginBottom: '6px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#5a8ab0', letterSpacing: '1.5px', padding: '10px 10px 6px', textTransform: 'uppercase' }}>
              {group.label}
            </div>
            {group.items.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  onClick={() => { setActiveTab(item.id); if (onClose) onClose(); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '10px 10px', borderRadius: '8px', border: 'none',
                    cursor: 'pointer', transition: 'all 0.12s', marginBottom: '2px',
                    backgroundColor: isActive ? T.emeraldDim : 'transparent',
                    color: isActive ? T.emerald : T.textSub,
                    textAlign: 'left',
                    borderLeft: isActive ? `2px solid ${T.emerald}` : '2px solid transparent',
                  }}
                  onMouseEnter={e => { if (!isActive) { e.currentTarget.style.backgroundColor = T.surfaceHover; e.currentTarget.style.color = T.text; } }}
                  onMouseLeave={e => { if (!isActive) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = T.textSub; } }}
                >
                  <Icon name={item.icon} size={17} color={isActive ? T.emerald : 'currentColor'} />
                  <span style={{ fontSize: '14px', fontWeight: isActive ? '600' : '400', flex: 1 }}>{item.label}</span>
                  {item.badge ? (
                    <span style={{
                      fontSize: '11px', fontWeight: '700',
                      color: isActive ? T.emerald : T.textMute,
                      backgroundColor: isActive ? T.emeraldDim : T.surfaceHi,
                      border: `1px solid ${isActive ? T.emeraldMid : T.border}`,
                      padding: '1px 7px', borderRadius: '10px', minWidth: '22px', textAlign: 'center',
                    }}>{item.badge}</span>
                  ) : null}
                </button>
              );
            })}
          </div>
        ))}
      <div style={{ padding: '16px 10px 20px', borderTop: `1px solid ${T.border}`, marginTop: '8px' }}>
        <button
          onClick={onSignOut}
          style={{
            width: '100%', padding: '10px 10px', border: `1px solid ${T.border}`,
            borderRadius: '8px', backgroundColor: 'transparent', color: T.textSub,
            cursor: 'pointer', fontSize: '14px', fontWeight: '400',
            display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.12s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = T.red; e.currentTarget.style.color = T.red; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.textSub; }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/><polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          Sign Out
        </button>
      </div>
      </nav>
    </aside>
  );
}

function MobileBottomNav({ activeTab, setActiveTab, onMenuOpen, upcomingCount }) {
  const bottomTabs = [
    { id: 'week',     label: 'This Week', icon: 'week'     },
    { id: 'calendar', label: 'Calendar',  icon: 'calendar', badge: upcomingCount },
    { id: 'news',     label: 'News',      icon: 'news'     },
    { id: 'watchlist',label: 'Watchlist', icon: 'watchlist'},
    { id: '__menu',   label: 'More',      icon: 'more'     },
  ];
  return (
    <nav style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100,
      backgroundColor: T.surface, borderTop: `1px solid ${T.border}`,
      display: 'flex', alignItems: 'stretch', height: '64px',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {bottomTabs.map(tab => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => tab.id === '__menu' ? onMenuOpen() : setActiveTab(tab.id)}
            style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: '4px', border: 'none',
              backgroundColor: 'transparent', cursor: 'pointer',
              color: isActive ? T.emerald : T.textMute,
              position: 'relative',
            }}
          >
            {tab.badge ? (
              <div style={{ position: 'relative' }}>
                <Icon name={tab.icon} size={20} color={isActive ? T.emerald : T.textMute} />
                <span style={{
                  position: 'absolute', top: '-4px', right: '-8px',
                  fontSize: '10px', fontWeight: '700', color: '#fff',
                  backgroundColor: T.red, borderRadius: '10px',
                  padding: '1px 5px', minWidth: '16px', textAlign: 'center',
                }}>{tab.badge}</span>
              </div>
            ) : (
              <Icon name={tab.icon} size={20} color={isActive ? T.emerald : T.textMute} />
            )}
            <span style={{ fontSize: '10px', fontWeight: isActive ? '700' : '400' }}>{tab.label}</span>
            {isActive && <div style={{ position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px', backgroundColor: T.emerald, borderRadius: '0 0 2px 2px' }} />}
          </button>
        );
      })}
    </nav>
  );
}

function normalizeCompanyForLookup(name) {
  return (name || '')
    .toLowerCase()
    .replace(/\b(inc\.?|corp\.?|ltd\.?|llc|co\.?|company|pharmaceuticals?|pharma|biosciences?|therapeutics?|sciences?|biotechnology|biotech|holdings?|group|ag|sa|nv|plc|laboratories?|labs?|international|technologies?)\b\.?/gi, '')
    .replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();
}

// Map a watchlist row to the corresponding fda_calendar entry so we can
// surface live status / decision_date / decision_type on the watchlist card.
// PRIMARY path: lookup by fda_calendar_id (FK). This is robust to date changes
// in fda_calendar — the FK never goes stale.
// LEGACY fallback: lookup by computed item_id string (cal_<drug>_<YYYYMMDD>).
// Used only for rows that haven't been migrated yet (fda_calendar_id IS NULL).
function findCalendarForWatchlistItem(w, calendarEntries) {
  if (!w || w.item_type !== 'calendar') return null;

  // Primary: FK lookup
  if (w.fda_calendar_id) {
    const byFk = calendarEntries.find(c => c.id === w.fda_calendar_id);
    if (byFk) return byFk;
  }

  // Fallback: string-id match (legacy, for rows missing the FK)
  return calendarEntries.find(c => {
    const calId = `cal_${(c.drug_name || '').replace(/[^a-z0-9]/gi, '').toLowerCase()}_${(c.decision_date || '').replace(/-/g, '')}`;
    return calId === w.item_id;
  }) || null;
}

// Status pill config for watchlist cards. Drives the colored badge on the right.
// Returns null when there's no calendar match (no pill shown rather than fake data).
function getWatchlistStatusPill(calEntry, theme) {
  if (!calEntry) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const decDate = calEntry.decision_date ? new Date(calEntry.decision_date + 'T00:00:00') : null;

  if (calEntry.status === 'pending') {
    if (decDate && decDate < today) {
      return { label: 'OVERDUE', color: theme.red, bg: 'rgba(239,68,68,0.12)' };
    }
    return { label: 'PENDING', color: theme.blue, bg: theme.blueDim };
  }

  if (calEntry.status === 'decided') {
    const dt = (calEntry.decision_type || '').toLowerCase();
    if (dt.includes('approv'))                          return { label: 'APPROVED',  color: theme.green,    bg: 'rgba(34,197,94,0.12)' };
    if (dt.includes('crl') || dt.includes('complete response')) return { label: 'CRL', color: theme.red,   bg: 'rgba(239,68,68,0.12)' };
    if (dt.includes('withdraw'))                        return { label: 'WITHDRAWN', color: theme.textMute, bg: 'rgba(138,138,149,0.12)' };
    return { label: 'DECIDED', color: theme.textSub, bg: theme.surfaceHi };
  }

  if (calEntry.status === 'in_trials') {
    return { label: 'PHASE 3', color: theme.purple, bg: 'rgba(167,139,250,0.12)' };
  }

  return null;
}

// Sort key for a watchlist row. Lower value = earlier in the list.
// Order of precedence: upcoming pending (soonest first) > overdue > decided (most recent first) > everything else
function watchlistSortKey(w, calendarEntries) {
  const cal = findCalendarForWatchlistItem(w, calendarEntries);
  if (!cal) return 99999999;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const decDate = cal.decision_date ? new Date(cal.decision_date + 'T00:00:00') : null;

  if (cal.status === 'pending' && decDate && decDate >= today) {
    return Math.floor((decDate - today) / (1000 * 60 * 60 * 24));
  }
  if (cal.status === 'pending' && decDate && decDate < today) {
    return 1000000 + Math.floor((today - decDate) / (1000 * 60 * 60 * 24));
  }
  if (cal.status === 'decided' && decDate) {
    return 2000000 - Math.floor(decDate.getTime() / (1000 * 60 * 60 * 24));
  }
  return 9000000;
}

function StockButton({ company, tickerMap, variant = 'inline' }) {
  const [open, setOpen] = useState(false);
  const getTicker = () => {
    if (!company || !tickerMap || Object.keys(tickerMap).length === 0) return null;
    const norm = normalizeCompanyForLookup(company);
    if (!norm || norm.length < 2) return null;
    if (tickerMap[norm]) return tickerMap[norm];
    const words = norm.split(/\s+/).filter(w => w.length > 3);
    for (const word of words) {
      for (const [key, ticker] of Object.entries(tickerMap)) {
        const keyWords = key.split(/\s+/);
        if (keyWords[0] === word || key === word) return ticker;
      }
    }
    return null;
  };
  const ticker = getTicker();
  const brokers = ticker ? [
    { name: 'Yahoo Finance', url: `https://finance.yahoo.com/quote/${ticker}` },
    { name: 'Robinhood',     url: `https://robinhood.com/stocks/${ticker}` },
    { name: 'Fidelity',      url: `https://digital.fidelity.com/prgw/digital/research/quote?symbol=${ticker}` },
    { name: 'Schwab',        url: `https://www.schwab.com/research/stocks/quotes/summary/${ticker}` },
  ] : null;
  if (!brokers) {
    const fallback = `https://www.google.com/search?q=${encodeURIComponent((company || '') + ' stock price')}`;
    if (variant === 'card') {
      return (
        <a href={fallback} target="_blank" rel="noopener noreferrer"
          style={{ display:'inline-flex',alignItems:'center',gap:'5px',color:T.emerald,fontSize:'14px',fontWeight:'600',textDecoration:'none',padding:'7px 16px',border:`1px solid ${T.emeraldMid}`,borderRadius:'7px',backgroundColor:T.emeraldDim,transition:'background 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.backgroundColor=T.emeraldMid}
          onMouseLeave={e=>e.currentTarget.style.backgroundColor=T.emeraldDim}>
          View Stock →
        </a>
      );
    }
    return <a href={fallback} target="_blank" rel="noopener noreferrer" style={{fontSize:'13px',color:T.emerald,textDecoration:'none',fontWeight:'600'}}>Stock →</a>;
  }
  const btnStyle = variant==='card'
    ? {display:'inline-flex',alignItems:'center',gap:'6px',color:T.emerald,fontSize:'14px',fontWeight:'700',padding:'7px 16px',border:`1px solid ${T.emeraldMid}`,borderRadius:'7px',backgroundColor:T.emeraldDim,cursor:'pointer',fontFamily:'inherit',transition:'background 0.15s'}
    : {fontSize:'13px',color:T.emerald,fontWeight:'700',background:'none',border:'none',cursor:'pointer',padding:0,fontFamily:'inherit'};
  return (
    <div style={{position:'relative',display:'inline-block'}}>
      <button onClick={e=>{e.stopPropagation();setOpen(o=>!o);}} style={btnStyle}
        onMouseEnter={e=>{if(variant==='card')e.currentTarget.style.backgroundColor=T.emeraldMid;}}
        onMouseLeave={e=>{if(variant==='card')e.currentTarget.style.backgroundColor=T.emeraldDim;}}>
        {ticker} ↗
      </button>
      {open && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:998}} onClick={e=>{e.stopPropagation();setOpen(false);}}/>
          <div style={{position:'absolute',right:0,top:'calc(100% + 6px)',zIndex:999,backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'10px',padding:'6px',minWidth:'200px',boxShadow:'0 16px 48px rgba(0,0,0,0.6)'}}>
            <div style={{fontSize:'11px',color:T.textMute,padding:'4px 10px 8px',fontWeight:'700',letterSpacing:'0.8px',borderBottom:`1px solid ${T.border}`,marginBottom:'4px'}}>TRADE {ticker}</div>
            {brokers.map(b => (
              <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" onClick={e=>{e.stopPropagation();setOpen(false);}}
                style={{display:'block',padding:'10px 10px',borderRadius:'6px',color:T.text,textDecoration:'none',fontSize:'14px',fontWeight:'400',transition:'background 0.1s'}}
                onMouseEnter={e=>e.currentTarget.style.backgroundColor=T.surfaceHover}
                onMouseLeave={e=>e.currentTarget.style.backgroundColor='transparent'}>
                {b.name}
              </a>
            ))}
            <div style={{fontSize:'12px',color:T.textMute,padding:'8px 10px 4px',borderTop:`1px solid ${T.border}`,marginTop:'4px',lineHeight:'1.5'}}>
              Not financial advice. Verify before trading.
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function App() {
  const [fdaDrugs, setFdaDrugs] = useState([]);
  const [trials, setTrials] = useState([]);
  const [calendarEntries, setCalendarEntries] = useState([]);
  const [pdufaReports, setPdufaReports] = useState({});  // keyed by fda_calendar_id
  const [expandedReports, setExpandedReports] = useState(new Set());  // calendar ids with report visible
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('week');
  const [calendarFilter, setCalendarFilter] = useState('all');
  const [session, setSession] = useState(undefined);
  const [showAuth, setShowAuth] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [userPlan, setUserPlan] = useState('starter');
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(null);
  const [expandedCard, setExpandedCard] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [showWatchlistLimit, setShowWatchlistLimit] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [tickerMap, setTickerMap] = useState({});
  const [insiderScores, setInsiderScores] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Re-entry guard: blocks the same item from being toggled twice in flight (prevents double-click dupes)
  const togglePendingRef = useRef(new Set());

  const isMobile = windowWidth < 768;
  const STARTER_LIMIT = 10;

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paymentSuccess = params.get('payment') === 'success';
    if (paymentSuccess) { setIsSubscribed(true); window.history.replaceState({}, '', '/'); }
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) {
        if (!paymentSuccess) checkSubscription(session.user.email);
        fetchData(); fetchWatchlist(session.user.id); fetchCalendar(); fetchTickers(); fetchInsiderScores(); fetchPdufaReports();
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) {
        if (!paymentSuccess) checkSubscription(session.user.email);
        fetchData(); fetchWatchlist(session.user.id); fetchCalendar(); fetchTickers(); fetchInsiderScores(); fetchPdufaReports();
      }
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function checkSubscription(email) {
    const { data } = await supabase.from('subscriptions').select('*').eq('email', email).eq('status', 'active').limit(1).maybeSingle();
    setIsSubscribed(!!data);
    if (data) setUserPlan(data.plan || 'starter');
  }
  async function fetchData() {
    setLoading(true);
    const { data: drugs } = await supabase.from('fda_drugs').select('*').order('created_at', { ascending: false }).limit(2000);
    const { data: clinicalTrials } = await supabase.from('clinical_trials').select('*').order('created_at', { ascending: false }).limit(2000);
    setFdaDrugs(drugs || []); setTrials(clinicalTrials || []); setLoading(false);
  }
  async function fetchCalendar() {
    const { data } = await supabase.from('fda_calendar').select('*').order('decision_date', { ascending: true });
    setCalendarEntries(data || []);
  }
  async function fetchPdufaReports() {
    const { data, error } = await supabase
      .from('pdufa_reports')
      .select('fda_calendar_id, report, generated_at, prompt_version');
    if (error) { console.error('Pdufa reports fetch error:', error); return; }
    const map = {};
    (data || []).forEach(r => { if (r.fda_calendar_id) map[r.fda_calendar_id] = r; });
    setPdufaReports(map);
  }
  async function fetchWatchlist(userId) {
    if (!userId) return;
    const { data, error } = await supabase.from('watchlists').select('*').eq('user_id', userId);
    if (error) console.error('Watchlist fetch error:', error);
    setWatchlist(data || []);
  }
  async function fetchTickers() {
    try {
      const { data } = await supabase.from('company_tickers').select('company_name,ticker');
      if (data && data.length > 0) {
        const map = {};
        data.forEach(({ company_name, ticker }) => { const norm = normalizeCompanyForLookup(company_name); if (norm) map[norm] = ticker; });
        setTickerMap(map);
      }
    } catch (e) { console.error('Error fetching tickers:', e); }
  }
  async function fetchInsiderScores() {
    try { const { data } = await supabase.from('insider_scores').select('*'); setInsiderScores(data || []); }
    catch (e) { console.error('Error fetching insider scores:', e); }
  }

  // Build the canonical item_id for a given row + type. Must match DB shape.
  function buildItemId(item, type) {
    if (type === 'fda')      return item.application_number || null;
    if (type === 'trials')   return item.trial_id || null;
    if (type === 'calendar') return `cal_${(item.drug_name || '').replace(/[^a-z0-9]/gi, '').toLowerCase()}_${(item.decision_date || '').replace(/-/g, '')}`;
    return null;
  }

  // Shared remove helper — used by both the toggle and the inline unstar button on the Watchlist tab.
  // Always filters by user_id to prevent cross-user deletes when RLS is off.
  async function removeFromWatchlist(itemId) {
    if (!itemId) return;
    if (togglePendingRef.current.has(itemId)) return;
    togglePendingRef.current.add(itemId);

    try {
      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      const snapshot = watchlist;
      setWatchlist(prev => prev.filter(w => w.item_id !== itemId));

      const { error } = await supabase
        .from('watchlists')
        .delete()
        .eq('user_id', user.id)
        .eq('item_id', itemId);

      if (error) {
        console.error('Watchlist delete error:', error);
        setWatchlist(snapshot); // rollback optimistic removal
      }
    } finally {
      togglePendingRef.current.delete(itemId);
    }
  }

  // Toggle watchlist entry for an item (add or remove based on current state).
  // Guards against double-click duplicates with togglePendingRef + DB-level unique constraint via upsert.
  async function toggleWatchlist(e, item, type) {
    if (e) e.stopPropagation();

    const itemId = buildItemId(item, type);
    if (!itemId) return;

    const isWatchedNow = watchlist.some(w => w.item_id === itemId);

    if (isWatchedNow) {
      await removeFromWatchlist(itemId);
      return;
    }

    // --- ADD path ---
    if (togglePendingRef.current.has(itemId)) return;
    togglePendingRef.current.add(itemId);

    try {
      if (userPlan === 'starter' && watchlist.length >= STARTER_LIMIT) {
        setShowWatchlistLimit(true);
        setTimeout(() => setShowWatchlistLimit(false), 4000);
        return;
      }

      const { data: authData } = await supabase.auth.getUser();
      const user = authData?.user;
      if (!user) return;

      let newItem;
      if (type === 'fda') {
        newItem = { user_id: user.id, item_id: itemId, item_type: 'fda', item_name: item.drug_name, manufacturer: item.manufacturer, risk_level: item.risk_level, application_number: item.application_number };
      } else if (type === 'trials') {
        newItem = { user_id: user.id, item_id: itemId, item_type: 'trials', item_name: item.title, manufacturer: item.company, risk_level: item.risk_level, application_number: item.trial_id };
      } else if (type === 'calendar') {
        // Calendar rows now write fda_calendar_id (FK to fda_calendar) for robust lookup.
        // item.id here is the fda_calendar.id passed in from the calendar tab card.
        newItem = {
          user_id: user.id,
          item_id: itemId,
          item_type: 'calendar',
          item_name: item.drug_name,
          manufacturer: item.company || '',
          risk_level: 'Medium',
          application_number: itemId,
          fda_calendar_id: item.id || null,
        };
      } else {
        return;
      }

      // Optimistic add
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
      const tempItem = { ...newItem, id: tempId };
      setWatchlist(prev => [...prev, tempItem]);

      // Upsert against the unique constraint (user_id, item_type, item_id).
      // If another click/tab already inserted this row, conflict resolves cleanly instead of throwing.
      const { data, error } = await supabase
        .from('watchlists')
        .upsert(newItem, { onConflict: 'user_id,item_type,item_id' })
        .select()
        .single();

      if (error) {
        console.error('Watchlist insert error:', error);
        setWatchlist(prev => prev.filter(w => w.id !== tempId));
      } else if (data) {
        // Replace temp with real row; also filter any other copy of this item_id so we never end up with two.
        setWatchlist(prev => {
          const cleaned = prev.filter(w => w.id !== tempId && w.item_id !== data.item_id);
          return [...cleaned, data];
        });
      }
    } finally {
      togglePendingRef.current.delete(itemId);
    }
  }

  const isWatched = (item, type) => {
    const itemId = buildItemId(item, type);
    if (!itemId) return false;
    return watchlist.some(w => w.item_id === itemId);
  };

  const handleSignOut = async () => { await supabase.auth.signOut(); setSession(null); setIsSubscribed(false); setWatchlist([]); setTickerMap({}); };
  const daysUntil = (dateStr) => { if (!dateStr) return null; const today = new Date(); today.setHours(0,0,0,0); return Math.ceil((new Date(dateStr) - today) / (1000*60*60*24)); };

  const getRiskColor = (r) => r==='Low' ? T.green : r==='High' ? T.red : T.amber;
  const getRiskBg    = (r) => r==='Low' ? 'rgba(34,197,94,0.08)' : r==='High' ? 'rgba(239,68,68,0.08)' : 'rgba(245,158,11,0.08)';
  const getRiskScore = (r) => r==='Low' ? 2 : r==='High' ? 4 : 3;
  const getRiskLabel = (r) => r==='Low' ? {label:'Low'} : r==='High' ? {label:'High'} : {label:'Med'};

  const getAppTypeColor = (appNum) => {
    if (!appNum) return { color:T.textSub, bg:'rgba(138,138,149,0.08)', label:'DRUG' };
    if (appNum.startsWith('BLA'))  return { color:T.purple, bg:'rgba(167,139,250,0.08)', label:'BIOLOGIC' };
    if (appNum.startsWith('NDA'))  return { color:T.blue,   bg:T.blueDim,                label:'BRAND' };
    if (appNum.startsWith('ANDA')) return { color:T.teal,   bg:'rgba(45,212,191,0.08)', label:'GENERIC' };
    return { color:T.textSub, bg:'rgba(138,138,149,0.08)', label:'DRUG' };
  };
  const getPhaseColor = (phase) => {
    if (!phase) return { color:T.textSub, bg:'rgba(138,138,149,0.08)' };
    if (phase.includes('PHASE1')||phase.includes('PHASE_1')) return { color:T.amber,  bg:'rgba(245,158,11,0.08)' };
    if (phase.includes('PHASE2')||phase.includes('PHASE_2')) return { color:T.blue,   bg:T.blueDim };
    if (phase.includes('PHASE3')||phase.includes('PHASE_3')) return { color:T.purple, bg:'rgba(167,139,250,0.08)' };
    if (phase.includes('PHASE4')||phase.includes('PHASE_4')) return { color:T.teal,   bg:'rgba(45,212,191,0.08)' };
    return { color:T.textSub, bg:'rgba(138,138,149,0.08)' };
  };
  const getStatusColor = (s) => { if (!s) return T.textSub; if (s==='RECRUITING') return T.green; if (s==='COMPLETED') return T.blue; if (s==='ACTIVE_NOT_RECRUITING') return T.amber; if (s==='TERMINATED') return T.red; return T.textSub; };

  const getUrgencyColor = (dateStr) => {
    if (!dateStr) return { color:T.textSub, bg:'rgba(74,74,82,0.12)', label:'TBD' };
    const days = daysUntil(dateStr);
    if (days < 0)   return { color:T.textMute, bg:'rgba(74,74,82,0.08)', label:'Past' };
    if (days <= 7)  return { color:T.red,   bg:'rgba(239,68,68,0.08)',  label:`${days}d` };
    if (days <= 30) return { color:T.amber, bg:'rgba(245,158,11,0.08)', label:`${days}d` };
    if (days <= 90) return { color:T.blue,  bg:T.blueDim,               label:`${days}d` };
    return { color:T.emerald, bg:T.emeraldDim, label:`${days}d` };
  };

  const getFilteredItems = () => {
    const items = activeTab === 'fda' ? fdaDrugs : trials;
    return items.filter(item => {
      if (riskFilter !== 'All' && item.risk_level !== riskFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        if (activeTab === 'fda') return (item.drug_name?.toLowerCase().includes(q)||item.manufacturer?.toLowerCase().includes(q)||item.application_number?.toLowerCase().includes(q)||item.ai_summary?.toLowerCase().includes(q));
        return (item.title?.toLowerCase().includes(q)||item.company?.toLowerCase().includes(q)||item.condition?.toLowerCase().includes(q)||item.phase?.toLowerCase().includes(q)||item.ai_summary?.toLowerCase().includes(q));
      }
      return true;
    });
  };

  const getFilteredCalendar = () => {
    const today = new Date().toISOString().split('T')[0];
    return calendarEntries.filter(entry => {
      if (calendarFilter === 'pending')  return entry.status === 'pending';
      if (calendarFilter === 'trials')   return entry.status === 'in_trials';
      if (calendarFilter === 'upcoming') return entry.status === 'pending' && entry.decision_date && entry.decision_date >= today;
      return true;
    });
  };

  const formatSummary = (summary) => {
    if (!summary) return null;
    const cleaned = summary.replace(/^#.*?\n/,'').replace(/\*\*/g,'').trim();
    const sections = [
      { key:'SUMMARY', color:T.textSub, label:'Summary' },
      { key:'RISK',    color:T.amber,   label:'Risk'    },
      { key:'INSIGHT', color:T.teal,    label:'Insight' },
    ];
    const parts = {};
    sections.forEach(({ key }) => { const regex = new RegExp(`${key}:\\s*(.+?)(?=SUMMARY:|RISK:|INSIGHT:|$)`,'s'); const match = cleaned.match(regex); if (match) parts[key] = match[1].trim(); });
    if (Object.keys(parts).length === 0) return <span style={{color:T.textSub,fontSize:'15px'}}>{cleaned}</span>;
    return (
      <div style={{display:'flex',flexDirection:'column',gap:'1px'}}>
        {sections.map(({ key, color, label }) =>
          parts[key] ? (
            <div key={key} style={{display:'flex',gap:isMobile?'10px':'16px',alignItems:'flex-start',padding:'14px 18px',backgroundColor:T.surfaceHi}}>
              <span style={{fontSize:'11px',fontWeight:'700',color,letterSpacing:'1px',textTransform:'uppercase',minWidth:isMobile?'48px':'60px',paddingTop:'2px',flexShrink:0}}>{label}</span>
              <span style={{color:'#d8e8ff',fontSize:'15px',lineHeight:'1.75',flex:1}}>{parts[key]}</span>
            </div>
          ) : null
        )}
      </div>
    );
  };

  if (currentPage === 'privacy')    return <PrivacyPolicy onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'terms')      return <TermsOfService onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'disclaimer') return <LegalDisclaimer onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'about')      return <AboutUs onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'contact')    return <Contact onBack={() => setCurrentPage(null)} />;
  if (currentPage === 'faq')        return <FAQ onBack={() => setCurrentPage(null)} />;
  if (selectedCompany) return <CompanyProfile companyName={selectedCompany} onBack={() => setSelectedCompany(null)} watchlist={watchlist} userPlan={userPlan} />;

  if (session === undefined) {
    return (
      <div style={{minHeight:'100vh',backgroundColor:T.bg,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:'24px'}}>
        <LogoMark size={56} />
        <div style={{textAlign:'center'}}>
          <div style={{fontSize:'22px',fontWeight:'800',color:T.text,letterSpacing:'-0.5px'}}>Alpha<span style={{color:T.emerald}}>Bio</span><span style={{fontWeight:'300',color:T.textSub}}>AI</span></div>
          <div style={{fontSize:'15px',color:T.textSub,marginTop:'6px'}}>Loading intelligence data...</div>
        </div>
        <div style={{width:'140px',height:'2px',backgroundColor:T.border,borderRadius:'1px',overflow:'hidden'}}>
          <div style={{height:'100%',width:'40%',backgroundColor:T.emerald,borderRadius:'1px',animation:'loadBar 1.4s ease-in-out infinite'}}/>
        </div>
      </div>
    );
  }

  if (!session) {
    if (showAuth) return <AuthPage onBack={() => setShowAuth(false)} />;
    return <LandingPage onEnterDashboard={() => setShowAuth(true)} onSignIn={() => setShowAuth(true)} onNavigate={setCurrentPage} />;
  }

  if (!isSubscribed) return <PricingPage session={session} onSignOut={handleSignOut} userPlan={userPlan} />;

  const filteredItems    = getFilteredItems();
  const filteredCalendar = getFilteredCalendar();
  const today            = new Date().toISOString().split('T')[0];
  const upcomingCount    = calendarEntries.filter(e => e.status==='pending' && e.decision_date && e.decision_date >= today).length;
  const urgentPDUFA      = calendarEntries.filter(e => { if (e.status!=='pending'||!e.decision_date) return false; const d=daysUntil(e.decision_date); return d!==null&&d>=0&&d<=7; });
  const navGroups        = getNavGroups(upcomingCount, watchlist.length);

  const renderCard = (item, i, type) => {
    const cardKey    = `${type}-${i}`;
    const isExpanded = expandedCard === cardKey;
    const riskColor  = getRiskColor(item.risk_level);
    const riskInfo   = getRiskLabel(item.risk_level);
    const riskScore  = getRiskScore(item.risk_level);
    const appType    = type === 'fda' ? getAppTypeColor(item.application_number) : null;
    const phaseColor = type === 'trials' ? getPhaseColor(item.phase) : null;
    const statusColor= type === 'trials' ? getStatusColor(item.status) : null;
    const watched    = isWatched(item, type);
    const company    = type === 'fda' ? item.manufacturer : item.company;
    return (
      <div key={cardKey} onClick={() => setExpandedCard(isExpanded ? null : cardKey)}
        style={{backgroundColor:isExpanded?T.surfaceHi:T.surface,borderRadius:'10px',border:`1px solid ${isExpanded?T.borderHi:T.border}`,borderLeft:`3px solid ${riskColor}`,transition:'background 0.15s,border-color 0.15s',cursor:'pointer',overflow:'hidden'}}
        onMouseEnter={e=>{if(!isExpanded){e.currentTarget.style.borderColor=T.borderHi;e.currentTarget.style.borderLeftColor=riskColor;}}}
        onMouseLeave={e=>{if(!isExpanded){e.currentTarget.style.borderColor=T.border;e.currentTarget.style.borderLeftColor=riskColor;}}}>
        <div style={{padding:isMobile?'16px':'20px 24px',display:'flex',alignItems:'center',gap:'16px'}}>
          <div style={{flexShrink:0,width:'40px',height:'40px',backgroundColor:getRiskBg(item.risk_level),border:`1px solid ${riskColor}30`,borderRadius:'8px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:'1px'}}>
            <span style={{fontSize:'15px',fontWeight:'800',color:riskColor,lineHeight:1}}>{riskScore}</span>
            <span style={{fontSize:'11px',color:T.textMute,lineHeight:1}}>/ 5</span>
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'5px',flexWrap:'wrap'}}>
              <h3 style={{margin:0,color:T.text,fontSize:isMobile?'16px':'17px',fontWeight:'700',letterSpacing:'-0.2px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',maxWidth:isMobile?'200px':'440px'}}>
                {type==='fda'?item.drug_name:item.title}
              </h3>
              {!isMobile && type==='fda' && appType && <span style={{fontSize:'11px',fontWeight:'700',color:appType.color,backgroundColor:appType.bg,padding:'2px 8px',borderRadius:'4px',letterSpacing:'0.5px',flexShrink:0}}>{appType.label}</span>}
              {!isMobile && type==='trials' && item.phase && item.phase!=="['Unknown']" && <span style={{fontSize:'11px',fontWeight:'700',color:phaseColor.color,backgroundColor:phaseColor.bg,padding:'2px 8px',borderRadius:'4px',flexShrink:0}}>{item.phase.replace(/[[\]']/g,'').trim()}</span>}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
              <span onClick={e=>{e.stopPropagation();setSelectedCompany(company);}} style={{fontSize:'14px',color:T.emerald,fontWeight:'500',cursor:'pointer',transition:'color 0.15s'}} onMouseEnter={e=>e.currentTarget.style.color='#34d399'} onMouseLeave={e=>e.currentTarget.style.color=T.emerald}>{company}</span>
              {!isMobile && type==='fda' && <span style={{fontSize:'13px',color:T.textMute}}>{item.application_number}</span>}
              {!isMobile && type==='trials' && item.status && <span style={{fontSize:'13px',color:statusColor,display:'flex',alignItems:'center',gap:'4px'}}><span style={{width:'5px',height:'5px',backgroundColor:statusColor,borderRadius:'50%',display:'inline-block'}}/>{item.status.replace(/_/g,' ')}</span>}
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:isMobile?'6px':'10px',flexShrink:0}}>
            {!isMobile && <div style={{display:'flex',gap:'2px'}}>{[1,2,3,4,5].map(n=><div key={n} style={{width:'3px',height:'16px',borderRadius:'2px',backgroundColor:n<=riskScore?riskColor:T.border}}/>)}</div>}
            <span style={{fontSize:'12px',fontWeight:'700',color:riskColor,backgroundColor:getRiskBg(item.risk_level),padding:'4px 9px',borderRadius:'5px',minWidth:'36px',textAlign:'center'}}>{riskInfo.label}</span>
            <button onClick={e=>toggleWatchlist(e,item,type)} title={watched?'Remove from watchlist':'Add to watchlist'}
              style={{backgroundColor:'transparent',border:'none',cursor:'pointer',fontSize:'17px',lineHeight:1,padding:'4px',color:watched?T.gold:T.textMute,transition:'color 0.15s'}}
              onMouseEnter={e=>e.currentTarget.style.color=T.gold}
              onMouseLeave={e=>{if(!watched)e.currentTarget.style.color=T.textMute;}}>{watched?'★':'☆'}</button>
            <span style={{fontSize:'12px',color:T.textMute,userSelect:'none'}}>{isExpanded?'▴':'▾'}</span>
          </div>
        </div>
        <div style={{height:'2px',backgroundColor:T.border,marginLeft:'74px',marginRight:'20px',borderRadius:'1px',overflow:'hidden'}}>
          <div style={{height:'100%',width:`${(riskScore/5)*100}%`,backgroundColor:riskColor,borderRadius:'1px',transition:'width 0.3s'}}/>
        </div>
        {isExpanded && (
          <div style={{marginTop:'1px'}} onClick={e=>e.stopPropagation()}>
            <div style={{padding:'14px 20px 10px',display:'flex',alignItems:'center',justifyContent:'space-between',backgroundColor:T.surfaceHi,flexWrap:'wrap',gap:'8px'}}>
              <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                <span style={{fontSize:'11px',fontWeight:'700',color:T.textSub,letterSpacing:'1px'}}>AI ANALYSIS</span>
                <span style={{width:'5px',height:'5px',borderRadius:'50%',backgroundColor:T.emerald,display:'inline-block',animation:'pulse 2s infinite'}}/>
              </div>
              <StockButton company={company} tickerMap={tickerMap} variant="card"/>
            </div>
            {formatSummary(item.ai_summary)}
            <div style={{padding:'10px 20px'}}><span style={{fontSize:'13px',color:T.textMute}}>ML risk score v2 — coming soon</span></div>
          </div>
        )}
      </div>
    );
  };

  // Toggle whether a calendar entry's PDUFA report is expanded inline.
  const togglePdufaReport = (calendarId) => {
    setExpandedReports(prev => {
      const next = new Set(prev);
      if (next.has(calendarId)) next.delete(calendarId);
      else next.add(calendarId);
      return next;
    });
  };

  // Render the Pre-PDUFA Positioning Report inline. Pro: all 7 sections.
  // Starter: Setup section visible, others grayed out + upgrade CTA.
  const renderPdufaReport = (calendarId) => {
    const reportRow = pdufaReports[calendarId];
    if (!reportRow || !reportRow.report) return null;
    const r = reportRow.report;

    const sections = [
      { key: 'setup',          label: 'SETUP',          color: T.textSub, alwaysVisible: true  },
      { key: 'drug',           label: 'THE DRUG',       color: T.blue,    alwaysVisible: false },
      { key: 'insider_signal', label: 'INSIDER SIGNAL', color: T.gold,    alwaysVisible: false },
      { key: 'stock_context',  label: 'STOCK CONTEXT',  color: T.teal,    alwaysVisible: false },
      { key: 'bull_case',      label: 'BULL CASE',      color: T.green,   alwaysVisible: false },
      { key: 'bear_case',      label: 'BEAR CASE',      color: T.red,     alwaysVisible: false },
      { key: 'bottom_line',    label: 'BOTTOM LINE',    color: T.emerald, alwaysVisible: false, emphasized: true },
    ];

    const isPro = userPlan === 'pro';

    return (
      <div style={{
        marginTop:'14px',
        backgroundColor:T.surfaceHi,
        border:`1px solid ${T.border}`,
        borderRadius:'10px',
        overflow:'hidden',
      }}>
        <div style={{padding:'14px 20px 12px',display:'flex',alignItems:'center',justifyContent:'space-between',borderBottom:`1px solid ${T.border}`,flexWrap:'wrap',gap:'8px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
            <span style={{fontSize:'11px',fontWeight:'700',color:T.textSub,letterSpacing:'1px'}}>PRE-PDUFA POSITIONING REPORT</span>
            <span style={{width:'5px',height:'5px',borderRadius:'50%',backgroundColor:T.emerald,display:'inline-block',animation:'pulse 2s infinite'}}/>
          </div>
          <span style={{fontSize:'11px',color:T.textMute}}>
            Generated {new Date(reportRow.generated_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
          </span>
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:'1px'}}>
          {sections.map(({ key, label, color, alwaysVisible, emphasized }) => {
            const visible = isPro || alwaysVisible;
            const text = r[key] || '';
            return (
              <div key={key} style={{
                display:'flex',
                gap:isMobile?'10px':'16px',
                alignItems:'flex-start',
                padding:emphasized && visible ? '18px 20px' : '14px 18px',
                backgroundColor:emphasized && visible ? 'rgba(16,185,129,0.06)' : T.surface,
                borderLeft:emphasized && visible ? `3px solid ${T.emerald}` : 'none',
              }}>
                <span style={{
                  fontSize:'11px',
                  fontWeight:'700',
                  color:visible ? color : T.textMute,
                  letterSpacing:'1px',
                  minWidth:isMobile?'82px':'118px',
                  lineHeight:emphasized && visible ? '28px' : '26px',
                  flexShrink:0,
                  opacity:visible ? 1 : 0.5,
                }}>{label}</span>
                <span style={{
                  color:visible ? (emphasized ? T.text : '#d8e8ff') : T.textMute,
                  fontSize:emphasized && visible ? '16px' : '15px',
                  fontWeight:emphasized && visible ? '500' : '400',
                  lineHeight:'1.75',
                  flex:1,
                  filter:visible ? 'none' : 'blur(3px)',
                  userSelect:visible ? 'auto' : 'none',
                }}>
                  {visible ? text : 'Upgrade to Pro to read this section. Includes drug background, insider signal analysis, stock context, bull and bear cases, and an actionable bottom line.'}
                </span>
              </div>
            );
          })}
        </div>

        {!isPro && (
          <div style={{
            padding:'14px 20px',
            backgroundColor:'rgba(16,185,129,0.07)',
            borderTop:`1px solid ${T.border}`,
            display:'flex',
            alignItems:'center',
            justifyContent:'space-between',
            gap:'12px',
            flexWrap:'wrap',
          }}>
            <div style={{flex:1,minWidth:'200px'}}>
              <div style={{fontSize:'15px',fontWeight:'700',color:T.text,marginBottom:'3px'}}>
                Unlock the full Pre-PDUFA Positioning Report
              </div>
              <div style={{fontSize:'13px',color:T.textSub,lineHeight:'1.5'}}>
                Institutional-grade biotech analysis on every upcoming FDA decision.
              </div>
            </div>
            <button
              onClick={() => setIsSubscribed(false)}
              style={{
                backgroundColor:T.emerald,
                color:'white',
                border:'none',
                padding:'10px 22px',
                borderRadius:'7px',
                cursor:'pointer',
                fontWeight:'700',
                fontSize:'14px',
                whiteSpace:'nowrap',
                fontFamily:'inherit',
              }}>
              Upgrade to Pro
            </button>
          </div>
        )}

        <div style={{padding:'10px 20px',borderTop:`1px solid ${T.border}`,fontSize:'11px',color:T.textMute,lineHeight:'1.6'}}>
          AI-generated analysis based on FDA filings, insider trade data, and stock price history. Not financial advice. Verify with original sources before trading.
        </div>
      </div>
    );
  };

  const renderCalendarCard = (entry, i) => {
    const urgency = getUrgencyColor(entry.decision_date);
    const isPDUFA = entry.status === 'pending';
    const days    = entry.decision_date ? daysUntil(entry.decision_date) : null;
    const isHot   = isPDUFA && days !== null && days >= 0 && days <= 7;
    const watched = isWatched(entry, 'calendar');
    return (
      <div key={i} style={{backgroundColor:T.surface,borderRadius:'10px',padding:isMobile?'14px':'18px 20px',border:`1px solid ${isHot?T.red+'40':T.border}`,borderLeft:`3px solid ${isPDUFA?urgency.color:T.purple}`,transition:'border-color 0.15s'}}
        onMouseEnter={e=>{e.currentTarget.style.borderColor=T.borderHi;e.currentTarget.style.borderLeftColor=isPDUFA?urgency.color:T.purple;}}
        onMouseLeave={e=>{e.currentTarget.style.borderColor=isHot?T.red+'40':T.border;e.currentTarget.style.borderLeftColor=isPDUFA?urgency.color:T.purple;}}>
        <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:'12px'}}>
          <div style={{flex:1,minWidth:0}}>
            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px',flexWrap:'wrap'}}>
              <h3 style={{margin:0,color:T.text,fontSize:isMobile?'15px':'16px',fontWeight:'600'}}>
                {entry.drug_name?.length>(isMobile?40:60)?entry.drug_name.substring(0,isMobile?40:60)+'...':entry.drug_name}
              </h3>
              <span style={{fontSize:'11px',fontWeight:'700',padding:'2px 8px',borderRadius:'4px',flexShrink:0,color:isPDUFA?urgency.color:T.purple,backgroundColor:isPDUFA?urgency.bg:'rgba(167,139,250,0.08)'}}>
                {isPDUFA?(entry.decision_type||'PDUFA'):'PHASE 3'}
              </span>
              {isHot && <span style={{width:'7px',height:'7px',backgroundColor:T.red,borderRadius:'50%',animation:'pulse 1s infinite',flexShrink:0}}/>}
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:entry.indication?'6px':'0'}}>
              <span onClick={()=>setSelectedCompany(entry.company)} style={{fontSize:'14px',color:T.emerald,fontWeight:'500',cursor:'pointer',transition:'color 0.15s'}} onMouseEnter={e=>e.currentTarget.style.color='#34d399'} onMouseLeave={e=>e.currentTarget.style.color=T.emerald}>
                {entry.company && entry.company!=='Unknown' ? (entry.company.length>40 ? entry.company.substring(0,40)+'...' : entry.company) : <span style={{color:T.textMute,fontStyle:'italic'}}>Company not listed</span>}
              </span>
            </div>
            {!isMobile && entry.indication && <p style={{margin:0,fontSize:'14px',color:T.textSub,lineHeight:'1.6'}}>{entry.indication.length>100?entry.indication.substring(0,100)+'...':entry.indication}</p>}
            {entry.so_what && (
              <div style={{display:'flex',alignItems:'flex-start',gap:'8px',marginTop:'10px',padding:'10px 14px',backgroundColor:'rgba(16,185,129,0.07)',border:'1px solid rgba(16,185,129,0.2)',borderRadius:'8px'}}>
                <span style={{color:T.emerald,fontSize:'14px',flexShrink:0,marginTop:'1px'}}>✦</span>
                <span style={{fontSize:isMobile?'13px':'15px',color:T.emerald,lineHeight:'1.55',fontWeight:'700'}}>{entry.so_what}</span>
              </div>
            )}
          </div>
          <div style={{flexShrink:0,textAlign:'right',minWidth:isMobile?'68px':'84px'}}>
            {isPDUFA && entry.decision_date ? (
              <>
                <div style={{backgroundColor:urgency.bg,border:`1px solid ${urgency.color}30`,borderRadius:'8px',padding:isMobile?'7px 10px':'10px 14px',marginBottom:'5px',display:'inline-block'}}>
                  <div style={{fontSize:isMobile?'16px':'20px',fontWeight:'800',color:urgency.color,lineHeight:1}}>{days!==null&&days>=0?urgency.label:'Past'}</div>
                  <div style={{fontSize:'10px',color:urgency.color,opacity:0.8,fontWeight:'700',letterSpacing:'0.5px',marginTop:'3px'}}>{days!==null&&days>=0?'DAYS':'DECISION'}</div>
                </div>
                <div style={{fontSize:'13px',color:T.textSub,marginBottom:'6px'}}>{new Date(entry.decision_date+'T00:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
              </>
            ) : (
              <div style={{backgroundColor:'rgba(167,139,250,0.08)',border:'1px solid rgba(167,139,250,0.2)',borderRadius:'8px',padding:'10px 12px',display:'inline-block',marginBottom:'6px'}}>
                <div style={{fontSize:'13px',color:T.purple,fontWeight:'700'}}>WATCH</div>
                <div style={{fontSize:'11px',color:T.textMute,marginTop:'3px'}}>Phase 3</div>
              </div>
            )}
            <div style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:'8px',marginTop:'4px'}}>
              <button
                onClick={e => toggleWatchlist(e, entry, 'calendar')}
                title={watched ? 'Remove from watchlist' : 'Add to watchlist'}
                style={{backgroundColor:'transparent',border:'none',cursor:'pointer',fontSize:'18px',lineHeight:1,padding:'4px',color:watched?T.gold:T.textMute,transition:'color 0.15s'}}
                onMouseEnter={e=>e.currentTarget.style.color=T.gold}
                onMouseLeave={e=>{if(!watched)e.currentTarget.style.color=T.textMute;}}>
                {watched?'★':'☆'}
              </button>
              <StockButton company={entry.company} tickerMap={tickerMap} variant="inline"/>
            </div>
            {userPlan==='pro' && isPDUFA && (() => {
              const score = insiderScores.find(s => s.decision_date===entry.decision_date && (s.company_name||'').toLowerCase().includes((entry.company||'').split(' ')[0].toLowerCase().slice(0,5)));
              if (!score) return null;
              const sc = score.label==='Bullish'?T.green:score.label==='Bearish'?T.red:T.amber;
              const sb = score.label==='Bullish'?'rgba(34,197,94,0.08)':score.label==='Bearish'?'rgba(239,68,68,0.08)':'rgba(245,158,11,0.08)';
              return <div style={{marginTop:'6px',backgroundColor:sb,border:`1px solid ${sc}30`,borderRadius:'7px',padding:'6px 10px',textAlign:'center'}} title={score.explanation}><div style={{fontSize:'14px',fontWeight:'800',color:sc,lineHeight:1}}>{score.score}/10</div><div style={{fontSize:'10px',color:sc,opacity:0.8,fontWeight:'700',letterSpacing:'0.5px',marginTop:'2px'}}>{score.label.toUpperCase()}</div></div>;
            })()}
          </div>
        </div>
        {entry.status === 'pending' && pdufaReports[entry.id] && (
          <div style={{paddingTop:'14px',marginTop:'14px',borderTop:`1px solid ${T.border}`}}>
            <button
              onClick={() => togglePdufaReport(entry.id)}
              style={{
                backgroundColor:'transparent',
                color:T.emerald,
                border:`1px solid ${T.emeraldMid}`,
                padding:'9px 18px',
                borderRadius:'7px',
                cursor:'pointer',
                fontSize:'14px',
                fontWeight:'700',
                fontFamily:'inherit',
                display:'inline-flex',
                alignItems:'center',
                gap:'8px',
                transition:'background 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = T.emeraldDim}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                <polyline points="14 2 14 8 20 8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {expandedReports.has(entry.id) ? 'Hide Report' : 'View Pre-PDUFA Report'}
            </button>
            {expandedReports.has(entry.id) && renderPdufaReport(entry.id)}
          </div>
        )}
      </div>
    );
  };

  // Dedicated renderer for watchlist rows that point at fda_calendar entries.
  // Shows: large bold drug name, company line, PDUFA date + countdown (for pending),
  // real status pill (Pending/Approved/CRL/etc) driven by fda_calendar.status.
  const renderWatchlistCalendarCard = (w, i) => {
    const cal = findCalendarForWatchlistItem(w, calendarEntries);
    const pill = getWatchlistStatusPill(cal, T);
    const days = cal && cal.decision_date && cal.status === 'pending' ? daysUntil(cal.decision_date) : null;
    const urgency = cal && cal.status === 'pending' ? getUrgencyColor(cal.decision_date) : null;
    const borderColor = pill ? pill.color : T.textMute;

    // Build the date/countdown line
    let dateLine = null;
    if (cal && cal.decision_date) {
      const formatted = new Date(cal.decision_date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
      if (cal.status === 'pending' && days !== null && days >= 0) {
        dateLine = (
          <>PDUFA <span style={{color:T.text,fontWeight:'700'}}>{formatted}</span>
          <span style={{color:T.textMute,margin:'0 8px'}}>·</span>
          <span style={{color:urgency.color,fontWeight:'800'}}>{days} day{days===1?'':'s'}</span></>
        );
      } else if (cal.status === 'pending' && days !== null && days < 0) {
        dateLine = (
          <>PDUFA was <span style={{color:T.text,fontWeight:'700'}}>{formatted}</span>
          <span style={{color:T.textMute,margin:'0 8px'}}>·</span>
          <span style={{color:T.red,fontWeight:'800'}}>{Math.abs(days)} day{Math.abs(days)===1?'':'s'} overdue</span></>
        );
      } else if (cal.status === 'decided') {
        dateLine = <>Decided <span style={{color:T.text,fontWeight:'700'}}>{formatted}</span></>;
      }
    }

    return (
      <div
        key={w.id || i}
        style={{
          backgroundColor: T.surface,
          borderRadius: '12px',
          padding: isMobile ? '18px 20px' : '22px 26px',
          border: `1px solid ${T.border}`,
          borderLeft: `4px solid ${borderColor}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          transition: 'border-color 0.15s, background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = T.borderHi; e.currentTarget.style.borderLeftColor = borderColor; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.borderLeftColor = borderColor; }}
      >
        <div style={{flex:1, minWidth:0}}>
          <div style={{
            fontSize: isMobile ? '20px' : '22px',
            fontWeight: '800',
            color: T.text,
            marginBottom: '8px',
            letterSpacing: '-0.3px',
            lineHeight: 1.25,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {w.item_name}
          </div>
          <div style={{
            fontSize: '14px',
            color: T.textSub,
            marginBottom: dateLine ? '10px' : 0,
            fontWeight: '500',
          }}>
            <span
              onClick={e => { e.stopPropagation(); if (w.manufacturer && w.manufacturer !== 'Unknown') setSelectedCompany(w.manufacturer); }}
              style={{
                color: (w.manufacturer && w.manufacturer !== 'Unknown') ? T.emerald : T.textMute,
                fontWeight: '600',
                cursor: (w.manufacturer && w.manufacturer !== 'Unknown') ? 'pointer' : 'default',
                fontStyle: (!w.manufacturer || w.manufacturer === 'Unknown') ? 'italic' : 'normal',
              }}
            >
              {w.manufacturer && w.manufacturer !== 'Unknown' ? w.manufacturer : 'Unknown company'}
            </span>
            <span style={{color: T.textMute, margin: '0 8px'}}>·</span>
            <span style={{color: T.textSub}}>
              {w.item_type === 'fda' ? 'FDA Drug' : w.item_type === 'calendar' ? 'FDA Calendar' : 'Clinical Trial'}
            </span>
          </div>
          {dateLine && (
            <div style={{fontSize: '14px', color: T.textSub, fontWeight: '500', letterSpacing: '0.1px'}}>
              {dateLine}
            </div>
          )}
        </div>
        <div style={{display:'flex', alignItems:'center', gap:'12px', flexShrink:0}}>
          {pill && (
            <span style={{
              fontSize: '12px',
              fontWeight: '800',
              color: pill.color,
              backgroundColor: pill.bg,
              padding: '7px 13px',
              borderRadius: '6px',
              letterSpacing: '0.9px',
              whiteSpace: 'nowrap',
            }}>
              {pill.label}
            </span>
          )}
          <button
            onClick={e => { e.stopPropagation(); removeFromWatchlist(w.item_id); }}
            title="Remove from watchlist"
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: T.gold,
              cursor: 'pointer',
              fontSize: '20px',
              padding: '4px',
              lineHeight: 1,
            }}
          >
            ★
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{minHeight:'100vh',backgroundColor:T.bg,color:T.text,fontFamily:"'Inter','DM Sans',system-ui,sans-serif",display:'flex'}}>

      {isMobile && sidebarOpen && (
        <>
          <div style={{position:'fixed',inset:0,zIndex:199,backgroundColor:'rgba(0,0,0,0.6)',backdropFilter:'blur(4px)'}} onClick={()=>setSidebarOpen(false)}/>
          <div style={{position:'fixed',left:0,top:0,bottom:0,width:SIDEBAR_W,zIndex:200}}>
            <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} userPlan={userPlan} session={session} onSignOut={handleSignOut} navGroups={navGroups} onClose={()=>setSidebarOpen(false)} isMobile={true}/>
          </div>
        </>
      )}

      {!isMobile && (
        <div style={{position:'fixed',left:0,top:0,bottom:0,width:SIDEBAR_W,zIndex:50}}>
          <Sidebar activeTab={activeTab} setActiveTab={tab=>{setActiveTab(tab);setSearchQuery('');setRiskFilter('All');setExpandedCard(null);}} userPlan={userPlan} session={session} onSignOut={handleSignOut} navGroups={navGroups} isMobile={false}/>
        </div>
      )}

      <div style={{width:isMobile?'100%':`calc(100% - ${SIDEBAR_W}px)`,marginLeft:isMobile?0:SIDEBAR_W,minHeight:'100vh',paddingBottom:isMobile?'72px':0}}>

        {isMobile && (
          <header style={{position:'sticky',top:0,zIndex:50,backgroundColor:T.surface,borderBottom:`1px solid ${T.border}`,padding:'0 16px',height:'56px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <button onClick={()=>setSidebarOpen(true)} style={{background:'none',border:'none',cursor:'pointer',padding:'4px',color:T.textSub}}>
              <Icon name="menu" size={22} color={T.textSub}/>
            </button>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              <LogoMark size={28}/>
              <span style={{fontSize:'16px',fontWeight:'800',color:T.text}}>Alpha<span style={{color:T.emerald}}>Bio</span><span style={{fontWeight:'300',color:T.textSub}}>AI</span></span>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              <span style={{fontSize:'11px',fontWeight:'700',color:userPlan==='pro'?T.gold:T.textSub,backgroundColor:userPlan==='pro'?'rgba(251,191,36,0.10)':T.surfaceHi,border:`1px solid ${userPlan==='pro'?'rgba(251,191,36,0.25)':T.border}`,padding:'3px 9px',borderRadius:'5px'}}>{userPlan.toUpperCase()}</span>
              <button onClick={handleSignOut} style={{background:'none',border:`1px solid ${T.border}`,borderRadius:'7px',cursor:'pointer',padding:'5px 10px',color:T.textSub,fontSize:'12px',fontWeight:'600',display:'flex',alignItems:'center',gap:'4px'}} onMouseEnter={e=>{e.currentTarget.style.borderColor=T.red;e.currentTarget.style.color=T.red;}} onMouseLeave={e=>{e.currentTarget.style.borderColor=T.border;e.currentTarget.style.color=T.textSub;}}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/><polyline points="16 17 21 12 16 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><line x1="21" y1="12" x2="9" y2="12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                Out
              </button>
            </div>
          </header>
        )}

        {!isMobile && (
          <header style={{position:'sticky',top:0,zIndex:48,backgroundColor:`${T.bg}f5`,borderBottom:`1px solid ${T.border}`,backdropFilter:'blur(16px)',padding:'0 32px',height:'56px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
              <span style={{width:'7px',height:'7px',borderRadius:'50%',backgroundColor:T.emerald,display:'inline-block',animation:'pulse 2s infinite'}}/>
              <span style={{fontSize:'13px',color:T.textSub,fontWeight:'600',letterSpacing:'0.5px'}}>LIVE</span>
              <span style={{color:T.border,margin:'0 8px'}}>·</span>
              <span style={{fontSize:'14px',color:T.textSub}}>{session.user.email}</span>
            </div>
            <div style={{fontSize:'16px',fontWeight:'600',color:T.textSub,letterSpacing:'-0.2px'}}>
              {navGroups.flatMap(g=>g.items).find(t=>t.id===activeTab)?.label || 'Dashboard'}
            </div>
          </header>
        )}

        {urgentPDUFA.length > 0 && (
          <div style={{backgroundColor:'rgba(239,68,68,0.06)',borderBottom:`1px solid rgba(239,68,68,0.2)`,padding:isMobile?'10px 16px':'12px 32px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap',cursor:'pointer'}} onClick={()=>setActiveTab('calendar')}>
            <span style={{width:8,height:8,borderRadius:'50%',backgroundColor:T.red,display:'inline-block',animation:'pulse 1s infinite',flexShrink:0}}/>
            <span style={{fontSize:14,fontWeight:700,color:T.red}}>{urgentPDUFA.length===1?'FDA DECISION THIS WEEK:':`${urgentPDUFA.length} FDA DECISIONS THIS WEEK:`}</span>
            <div style={{display:'flex',gap:8,flexWrap:'wrap',flex:1}}>
              {urgentPDUFA.slice(0,isMobile?1:3).map((e,i)=>{const d=daysUntil(e.decision_date);return(<span key={i} style={{fontSize:14,color:T.textSub}}><span style={{color:T.text,fontWeight:600}}>{e.drug_name?.substring(0,25)}</span><span style={{color:T.red,fontWeight:700,marginLeft:6}}>{d===0?'TODAY':d===1?'tomorrow':`in ${d} days`}</span>{i<urgentPDUFA.length-1&&i<(isMobile?0:2)&&<span style={{color:T.textMute,marginLeft:8}}>·</span>}</span>);})}
            </div>
            <span style={{fontSize:13,color:T.textMute,flexShrink:0}}>View calendar →</span>
          </div>
        )}

        <div style={{borderBottom:`1px solid ${T.border}`,display:'grid',gridTemplateColumns:isMobile?'repeat(2,1fr)':'repeat(4,1fr)'}}>
          {[
            {label:'FDA Drug Records',   value:fdaDrugs.length,  color:T.blue},
            {label:'Clinical Trials',    value:trials.length,    color:T.purple},
            {label:'Upcoming Decisions', value:upcomingCount,    color:T.red},
            {label:'Watching',           value:userPlan==='starter'?`${watchlist.length}/${STARTER_LIMIT}`:watchlist.length, color:T.gold},
          ].map((stat,i)=>(
            <div key={i} style={{padding:isMobile?'16px 18px':'20px 28px',borderRight:isMobile?(i%2===0?`1px solid ${T.border}`:'none'):(i<3?`1px solid ${T.border}`:'none'),borderBottom:isMobile&&i<2?`1px solid ${T.border}`:'none',display:'flex',flexDirection:'column',gap:'5px'}}>
              <div style={{fontSize:isMobile?'22px':'26px',fontWeight:'800',color:stat.color,letterSpacing:'-0.5px',lineHeight:1}}>{stat.value}</div>
              <div style={{fontSize:isMobile?'12px':'14px',color:T.textSub}}>{stat.label}</div>
            </div>
          ))}
        </div>

        <div style={{padding:isMobile?'20px 16px 40px':'28px 32px 48px'}}>
          <NaturalSearch plan={userPlan} isMobile={isMobile} onTabChange={setActiveTab}/>

          {showWatchlistLimit && (
            <div style={{position:'fixed',bottom:isMobile?'80px':'28px',left:'50%',transform:'translateX(-50%)',zIndex:999,backgroundColor:T.surfaceHi,border:`1px solid ${T.gold}40`,borderRadius:'10px',padding:'14px 22px',display:'flex',alignItems:'center',gap:'12px',boxShadow:'0 8px 32px rgba(0,0,0,0.5)',whiteSpace:'nowrap'}}>
              <span style={{fontSize:'16px',color:T.gold}}>★</span>
              <div>
                <div style={{fontSize:'15px',fontWeight:'600',color:T.text}}>Watchlist limit reached</div>
                <div style={{fontSize:'14px',color:T.textSub,marginTop:'2px'}}>Starter plan: {STARTER_LIMIT} items. Upgrade to Pro for unlimited.</div>
              </div>
            </div>
          )}

          {activeTab==='week' && <WeeklyBrief session={session} plan={userPlan} onTabChange={setActiveTab} watchlist={watchlist} onUpgrade={() => setIsSubscribed(false)}/>}
          {activeTab==='portfolio' && <PortfolioTracker session={session} plan={userPlan}/>}
          {activeTab==='insider'   && <InsiderTrading session={session} plan={userPlan}/>}
          {activeTab==='failures'  && <FDAFailures session={session} plan={userPlan}/>}
          {activeTab==='earnings'  && <EarningsCollider session={session} plan={userPlan}/>}
          {activeTab==='adcom'     && <AdComTracker session={session} plan={userPlan}/>}
          {activeTab==='news'      && <NewsFeed session={session} plan={userPlan}/>}
          {activeTab==='alerts'    && <PriceAlerts session={session} plan={userPlan}/>}

          {activeTab==='calendar' && (
            <div>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:'24px',flexWrap:'wrap',gap:'12px'}}>
                <div>
                  <h2 style={{margin:'0 0 6px',color:T.text,fontSize:isMobile?'22px':'28px',fontWeight:'800',letterSpacing:'-0.5px'}}>FDA Intelligence Calendar</h2>
                  <p style={{margin:0,color:T.textSub,fontSize:'15px'}}>PDUFA decision dates and Phase 3 watch items.</p>
                </div>
                {!isMobile && (
                  <div style={{display:'flex',gap:'16px'}}>
                    {[{color:T.red,label:'≤ 7 days'},{color:T.amber,label:'≤ 30 days'},{color:T.blue,label:'≤ 90 days'},{color:T.emerald,label:'Later'}].map((l,i)=>(
                      <div key={i} style={{display:'flex',alignItems:'center',gap:'6px'}}>
                        <div style={{width:'8px',height:'8px',borderRadius:'50%',backgroundColor:l.color}}/>
                        <span style={{fontSize:'14px',color:T.textSub}}>{l.label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{display:'flex',gap:'4px',marginBottom:'24px',borderBottom:`1px solid ${T.border}`,overflowX:'auto'}}>
                {[{id:'upcoming',label:`Upcoming (${upcomingCount})`},{id:'pending',label:`Pending (${calendarEntries.filter(e=>e.status==='pending').length})`},{id:'trials',label:`Phase 3 (${calendarEntries.filter(e=>e.status==='in_trials').length})`},{id:'all',label:`All (${calendarEntries.length})`}].map(f=>{
                  const isA=calendarFilter===f.id;
                  return <button key={f.id} onClick={()=>setCalendarFilter(f.id)} style={{padding:isMobile?'10px 14px 9px':'12px 20px 10px',border:'none',borderBottom:`2px solid ${isA?T.emerald:'transparent'}`,backgroundColor:'transparent',color:isA?T.text:T.textSub,cursor:'pointer',fontSize:isMobile?'13px':'15px',fontWeight:isA?'700':'400',transition:'all 0.15s',marginBottom:'-1px',whiteSpace:'nowrap'}}>{f.label}</button>;
                })}
              </div>
              {filteredCalendar.length===0 ? (
                <div style={{textAlign:'center',padding:'60px',color:T.textMute}}><div style={{fontSize:'36px',marginBottom:'14px'}}>—</div><div style={{fontSize:'16px'}}>No entries for this filter</div></div>
              ) : (
                <div style={{display:'grid',gap:'10px'}}>{filteredCalendar.map((entry,i)=>renderCalendarCard(entry,i))}</div>
              )}
              <div style={{marginTop:'32px',padding:'16px 20px',backgroundColor:T.surface,border:`1px solid ${T.border}`,borderRadius:'10px'}}>
                <p style={{margin:0,fontSize:'14px',color:T.textSub,lineHeight:'1.7'}}>Data sources: PDUFA dates from RTTNews, SEC EDGAR, and FDA.gov. Phase 3 items from ClinicalTrials.gov. Dates are estimates — always verify with official sources. Not financial advice.</p>
              </div>
            </div>
          )}

          {activeTab==='watchlist' && (
            <div>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'24px',flexWrap:'wrap',gap:'10px'}}>
                <div>
                  <h2 style={{margin:'0 0 6px',color:T.text,fontSize:isMobile?'22px':'28px',fontWeight:'800',letterSpacing:'-0.5px'}}>My Watchlist</h2>
                  <p style={{margin:0,color:T.textSub,fontSize:'15px'}}>{userPlan==='starter'?`${watchlist.length} of ${STARTER_LIMIT} slots used`:`${watchlist.length} items tracked`}</p>
                </div>
                {userPlan==='starter' && <div style={{fontSize:'14px',color:T.textSub,backgroundColor:T.surface,border:`1px solid ${T.border}`,padding:'8px 16px',borderRadius:'7px'}}>{watchlist.length}/{STARTER_LIMIT} — <span style={{color:T.emerald,cursor:'pointer',fontWeight:'600'}}>Upgrade to Pro</span></div>}
              </div>
              {watchlist.length===0 ? (
                <div style={{textAlign:'center',padding:'80px 40px'}}>
                  <div style={{fontSize:'40px',marginBottom:'16px',color:T.textMute}}>☆</div>
                  <div style={{fontSize:'19px',fontWeight:'700',color:T.textSub,marginBottom:'8px'}}>No items in your watchlist</div>
                  <div style={{fontSize:'15px',marginBottom:'28px',color:T.textMute}}>Star any drug or trial to track it here</div>
                  <button onClick={()=>setActiveTab('fda')} style={{backgroundColor:T.emerald,color:'white',border:'none',padding:'12px 26px',borderRadius:'8px',cursor:'pointer',fontWeight:'700',fontSize:'15px'}}>Browse FDA Drugs →</button>
                </div>
              ) : (
                <div style={{display:'grid',gap:'12px'}}>
                  {[...watchlist]
                    .sort((a, b) => watchlistSortKey(a, calendarEntries) - watchlistSortKey(b, calendarEntries))
                    .map((w, i) => {
                      // For fda/trials watchlist rows, render using the existing card component
                      const fullItem = w.item_type === 'fda'
                        ? fdaDrugs.find(d => d.application_number === w.item_id)
                        : w.item_type === 'trials'
                          ? trials.find(t => t.trial_id === w.item_id)
                          : null;
                      if (fullItem) return renderCard(fullItem, i, w.item_type);
                      // Calendar rows get the new enriched card
                      return renderWatchlistCalendarCard(w, i);
                    })}
                </div>
              )}
            </div>
          )}

          {(activeTab==='fda'||activeTab==='trials') && (
            <>
              <div style={{marginBottom:'20px'}}>
                <h2 style={{margin:'0 0 6px',color:T.text,fontSize:isMobile?'20px':'24px',fontWeight:'800',letterSpacing:'-0.5px'}}>{activeTab==='fda'?'FDA Drug Database':'Clinical Trials Database'}</h2>
                <p style={{margin:0,color:T.textSub,fontSize:'16px'}}>{activeTab==='fda'?`${fdaDrugs.length} FDA-approved and pipeline drugs with AI analysis`:`${trials.length} clinical trials across 8 disease areas`}</p>
              </div>
              <div style={{display:'flex',gap:'10px',marginBottom:'16px',flexDirection:isMobile?'column':'row',alignItems:isMobile?'stretch':'center'}}>
                <div style={{flex:1,minWidth:'200px',position:'relative'}}>
                  <span style={{position:'absolute',left:'14px',top:'50%',transform:'translateY(-50%)',color:T.textMute,fontSize:'15px'}}>⌕</span>
                  <input type="text" placeholder={activeTab==='fda'?'Search drugs, manufacturers...':'Search trials, companies...'} value={searchQuery} onChange={e=>setSearchQuery(e.target.value)}
                    style={{width:'100%',padding:'11px 16px 11px 38px',borderRadius:'8px',border:`1px solid ${T.border}`,backgroundColor:T.surface,color:T.text,fontSize:'15px',outline:'none',boxSizing:'border-box',transition:'border-color 0.15s'}}
                    onFocus={e=>e.target.style.borderColor=T.emerald} onBlur={e=>e.target.style.borderColor=T.border}/>
                </div>
                <div style={{display:'flex',gap:'4px',flexWrap:'wrap'}}>
                  {['All','Low','Medium','High'].map(risk=>{
                    const isActive=riskFilter===risk;
                    const rColor=risk==='Low'?T.green:risk==='Medium'?T.amber:risk==='High'?T.red:T.emerald;
                    return <button key={risk} onClick={()=>setRiskFilter(risk)} style={{padding:'10px 16px',borderRadius:'7px',cursor:'pointer',fontSize:'14px',fontWeight:'500',transition:'all 0.15s',border:`1px solid ${isActive?rColor+'50':T.border}`,backgroundColor:isActive?`${rColor}12`:'transparent',color:isActive?rColor:T.textSub}}>{risk}</button>;
                  })}
                </div>
              </div>
              <div style={{marginBottom:'18px',display:'flex',alignItems:'center',gap:'8px',flexWrap:'wrap'}}>
                <span style={{fontSize:'14px',color:T.textSub}}>{filteredItems.length} results</span>
                {searchQuery && <span style={{fontSize:'14px',color:T.textSub}}>for "{searchQuery}"</span>}
                {riskFilter!=='All' && <span style={{fontSize:'13px',color:getRiskColor(riskFilter),backgroundColor:getRiskBg(riskFilter),padding:'3px 11px',borderRadius:'5px',fontWeight:'700'}}>{riskFilter} risk</span>}
                {!isMobile && <span style={{fontSize:'13px',color:T.textMute,marginLeft:'auto'}}>★ to watch · click company to profile</span>}
              </div>
              {loading ? (
                <div style={{textAlign:'center',padding:'80px',color:T.textMute}}>
                  <div style={{fontSize:'15px',marginBottom:'14px'}}>Loading data...</div>
                  <div style={{width:'80px',height:'2px',backgroundColor:T.border,borderRadius:'1px',overflow:'hidden',margin:'0 auto'}}>
                    <div style={{height:'100%',width:'50%',backgroundColor:T.emerald,animation:'loadBar 1.4s ease-in-out infinite'}}/>
                  </div>
                </div>
              ) : filteredItems.length===0 ? (
                <div style={{textAlign:'center',padding:'80px',color:T.textMute}}>
                  <div style={{fontSize:'15px',color:T.textSub,marginBottom:'6px'}}>No results found</div>
                  <div style={{fontSize:'14px'}}>Try a different search term or filter</div>
                </div>
              ) : (
                <div style={{display:'grid',gap:'8px'}}>{filteredItems.map((item,i)=>renderCard(item,i,activeTab))}</div>
              )}
            </>
          )}
        </div>
      </div>

      {isMobile && <MobileBottomNav activeTab={activeTab} setActiveTab={tab=>{setActiveTab(tab);setSearchQuery('');setRiskFilter('All');setExpandedCard(null);}} onMenuOpen={()=>setSidebarOpen(true)} upcomingCount={upcomingCount}/>}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        body { background: #060d1e; margin: 0; }
        input::placeholder { color: #4a6a8a; }
        select option { background: #0d1a2d; color: #f0f6ff; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #1e3a5f; border-radius: 2px; }
        ::-webkit-scrollbar-thumb:hover { background: #2d5a8e; }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
        @keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }
      `}</style>
    </div>
  );
}

export default App;